import { useEffect, useState } from "react";
import { Checkbox } from "./ui/checkbox";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Slider } from "./ui/slider";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import {
    DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuLabel,
    DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { CATEGORY_META } from "../lib/api";
import { ChevronDown, RotateCcw } from "lucide-react";

const DEFAULTS = {
    categories: [],
    sectors: [],
    min_yield: "",
    max_yield: "",
    max_payout: "",
    min_dgr_5y: "",
    min_cagr_5y: "",
    min_streak: "",
    max_pe: "",
    min_market_cap: "",
    search: "",
};

export default function FiltersPanel({ meta, value, onChange }) {
    const [local, setLocal] = useState(value);

    useEffect(() => setLocal(value), [value]);

    const update = (patch) => {
        const next = { ...local, ...patch };
        setLocal(next);
        onChange(next);
    };

    const toggleArray = (key, item) => {
        const set = new Set(local[key] || []);
        set.has(item) ? set.delete(item) : set.add(item);
        update({ [key]: Array.from(set) });
    };

    const reset = () => onChange({ ...DEFAULTS });

    return (
        <aside className="w-full lg:w-72 shrink-0" data-testid="filters-panel">
            <div className="rounded-2xl border border-border bg-background">
                <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                    <div>
                        <div className="overline">Filters</div>
                        <div className="font-heading text-lg font-semibold tracking-tight mt-0.5">Refine</div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={reset} data-testid="reset-filters-btn">
                        <RotateCcw className="h-3.5 w-3.5" />
                        Reset
                    </Button>
                </div>

                <div className="p-5 space-y-5">
                    <div>
                        <Label className="text-xs font-semibold">Search</Label>
                        <Input
                            value={local.search}
                            onChange={(e) => update({ search: e.target.value })}
                            placeholder="Ticker or name…"
                            className="mt-2"
                            data-testid="search-input"
                        />
                    </div>

                    <div>
                        <Label className="text-xs font-semibold mb-2 block">Category</Label>
                        <div className="space-y-2">
                            {(meta?.categories || []).map((c) => (
                                <label
                                    key={c}
                                    className="flex items-center gap-2.5 text-sm cursor-pointer hover:text-foreground"
                                    data-testid={`category-checkbox-${c}`}
                                >
                                    <Checkbox
                                        checked={local.categories.includes(c)}
                                        onCheckedChange={() => toggleArray("categories", c)}
                                    />
                                    <span className="font-medium">{CATEGORY_META[c]?.label || c}</span>
                                    <span className="text-xs text-muted-foreground">({CATEGORY_META[c]?.years})</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <Accordion type="multiple" defaultValue={["yield", "growth"]} className="w-full">
                        <AccordionItem value="yield" className="border-border">
                            <AccordionTrigger className="text-xs font-semibold uppercase tracking-wider hover:no-underline">
                                Yield & payout
                            </AccordionTrigger>
                            <AccordionContent className="space-y-3 pt-1">
                                <NumPair
                                    label="Yield % range"
                                    minVal={local.min_yield}
                                    maxVal={local.max_yield}
                                    onMin={(v) => update({ min_yield: v })}
                                    onMax={(v) => update({ max_yield: v })}
                                    testid="yield"
                                />
                                <NumSingle
                                    label="Max payout ratio (%)"
                                    val={local.max_payout}
                                    onChange={(v) => update({ max_payout: v })}
                                    testid="max-payout"
                                />
                            </AccordionContent>
                        </AccordionItem>

                        <AccordionItem value="growth" className="border-border">
                            <AccordionTrigger className="text-xs font-semibold uppercase tracking-wider hover:no-underline">
                                Growth
                            </AccordionTrigger>
                            <AccordionContent className="space-y-3 pt-1">
                                <NumSingle label="Min 5Y DGR (%)"    val={local.min_dgr_5y}  onChange={(v) => update({ min_dgr_5y: v })}  testid="min-dgr-5y" />
                                <NumSingle label="Min 5Y CAGR (%)"   val={local.min_cagr_5y} onChange={(v) => update({ min_cagr_5y: v })} testid="min-cagr-5y" />
                                <NumSingle label="Min growth streak (yrs)" val={local.min_streak} onChange={(v) => update({ min_streak: v })} testid="min-streak" />
                            </AccordionContent>
                        </AccordionItem>

                        <AccordionItem value="valuation" className="border-border">
                            <AccordionTrigger className="text-xs font-semibold uppercase tracking-wider hover:no-underline">
                                Valuation & size
                            </AccordionTrigger>
                            <AccordionContent className="space-y-3 pt-1">
                                <NumSingle label="Max P/E"            val={local.max_pe}         onChange={(v) => update({ max_pe: v })}         testid="max-pe" />
                                <NumSingle label="Min market cap ($M)" val={local.min_market_cap} onChange={(v) => update({ min_market_cap: v })} testid="min-market-cap" />
                            </AccordionContent>
                        </AccordionItem>

                        <AccordionItem value="sector" className="border-border border-b-0">
                            <AccordionTrigger className="text-xs font-semibold uppercase tracking-wider hover:no-underline">
                                Sector
                            </AccordionTrigger>
                            <AccordionContent className="pt-1">
                                <SectorDropdown
                                    options={meta?.sectors || []}
                                    selected={local.sectors}
                                    onToggle={(s) => toggleArray("sectors", s)}
                                    onClear={() => update({ sectors: [] })}
                                />
                            </AccordionContent>
                        </AccordionItem>
                    </Accordion>
                </div>
            </div>
        </aside>
    );
}

function NumSingle({ label, val, onChange, testid }) {
    return (
        <div>
            <Label className="text-xs">{label}</Label>
            <Input
                type="number"
                value={val}
                onChange={(e) => onChange(e.target.value)}
                placeholder="—"
                className="mt-1.5 h-9"
                data-testid={`filter-${testid}`}
            />
        </div>
    );
}

function NumPair({ label, minVal, maxVal, onMin, onMax, testid }) {
    return (
        <div>
            <Label className="text-xs">{label}</Label>
            <div className="grid grid-cols-2 gap-2 mt-1.5">
                <Input
                    type="number"
                    value={minVal}
                    onChange={(e) => onMin(e.target.value)}
                    placeholder="min"
                    className="h-9"
                    data-testid={`filter-${testid}-min`}
                />
                <Input
                    type="number"
                    value={maxVal}
                    onChange={(e) => onMax(e.target.value)}
                    placeholder="max"
                    className="h-9"
                    data-testid={`filter-${testid}-max`}
                />
            </div>
        </div>
    );
}

function SectorDropdown({ options, selected, onToggle, onClear }) {
    const count = selected.length;
    const label =
        count === 0 ? "All sectors"
        : count === 1 ? selected[0]
        : `${count} sectors selected`;

    return (
        <div className="space-y-2">
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button
                        variant="outline"
                        className="w-full justify-between h-9 font-normal"
                        data-testid="sector-dropdown-trigger"
                    >
                        <span className={count === 0 ? "text-muted-foreground" : ""}>{label}</span>
                        <ChevronDown className="h-3.5 w-3.5 opacity-60" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    align="start"
                    className="w-[var(--radix-dropdown-menu-trigger-width)] max-h-72 overflow-y-auto"
                >
                    <DropdownMenuLabel className="text-xs">Filter by sector</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {options.length === 0 && (
                        <div className="px-2 py-1.5 text-xs text-muted-foreground">No sectors available</div>
                    )}
                    {options.map((s) => (
                        <DropdownMenuCheckboxItem
                            key={s}
                            checked={selected.includes(s)}
                            onCheckedChange={() => onToggle(s)}
                            onSelect={(e) => e.preventDefault()}
                            className="cursor-pointer"
                            data-testid={`sector-option-${s.replace(/\s+/g, '-').toLowerCase()}`}
                        >
                            {s}
                        </DropdownMenuCheckboxItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>

            {count > 0 && (
                <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground tabular-nums">{count} selected</span>
                    <button
                        type="button"
                        onClick={onClear}
                        className="text-primary hover:underline"
                        data-testid="sector-clear-btn"
                    >
                        Clear
                    </button>
                </div>
            )}
        </div>
    );
}

export { DEFAULTS };
